// AI노트 폴더 트리(최대 3단계) — 화면용 순수 함수.
// 규칙의 정본은 서버(backend/services/meeting_folder_tree.py)다. 여기 값은 화면 표시용 사본이다.
// 서버는 GET /api/meetings/folders 를 트리 순서(DFS)의 평면 배열로 준다. 1단계이면서 만든 사람이 소유자인
// 폴더는 트리 키(parent_id·depth·can_* …)를 싣지 않는다 — 그때는 아래 기본값이 서버 판정과 같다.

export const MAX_FOLDER_DEPTH = 3;
export const FOLDER_EXPANDED_KEY = 'gw.meetings.folderExpanded';

const keyOf = (id) => String(id);

// 평면 목록 → 트리. parent_id 가 없거나 부모가 목록에 없으면 1단계로 취급한다(순환도 1단계로 끊는다).
// 반환: { roots: [node], byKey: Map<string, node> } · node = { key, folder, parentKey, depth, children }
export function buildTree(folders) {
  const list = (Array.isArray(folders) ? folders : []).filter((f) => f && f.id != null);
  const byKey = new Map();
  list.forEach((f) => {
    const k = keyOf(f.id);
    if (!byKey.has(k)) byKey.set(k, { key: k, folder: f, parentKey: null, depth: 1, children: [] });
  });
  const roots = [];
  byKey.forEach((node) => {
    const pid = node.folder.parent_id;
    const pk = pid == null ? null : keyOf(pid);
    const parent = pk != null && pk !== node.key ? byKey.get(pk) : null;
    if (parent) {
      node.parentKey = pk;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const reached = new Set();
  const visit = (node, depth) => {
    reached.add(node.key);
    node.depth = depth;
    node.children = node.children.filter((c) => !reached.has(c.key));
    node.children.forEach((c) => visit(c, depth + 1));
  };
  roots.forEach((r) => visit(r, 1));
  // 순환으로 1단계에서 닿지 않는 폴더 — 부모에서 떼어 1단계로 올린다(화면에서 사라지지 않게).
  byKey.forEach((node) => {
    if (reached.has(node.key)) return;
    const parent = node.parentKey != null ? byKey.get(node.parentKey) : null;
    if (parent) parent.children = parent.children.filter((c) => c !== node);
    node.parentKey = null;
    roots.push(node);
    visit(node, 1);
  });
  return { roots, byKey };
}

// 트리 순서로 펼친 목록. isExpanded(key) 가 false 인 폴더의 하위는 건너뛴다(주지 않으면 모두 펼침).
// 항목 = { key, folder, depth, parentKey, hasChildren }
export function flattenTree(folders, isExpanded) {
  const { roots } = buildTree(folders);
  const out = [];
  const walk = (node) => {
    out.push({ key: node.key, folder: node.folder, depth: node.depth, parentKey: node.parentKey, hasChildren: node.children.length > 0 });
    if (node.children.length > 0 && (typeof isExpanded !== 'function' || isExpanded(node.key))) {
      node.children.forEach(walk);
    }
  };
  roots.forEach(walk);
  return out;
}

// 1단계부터 그 폴더까지의 폴더 객체 목록(없으면 []).
export function pathOf(folders, id) {
  if (id == null) return [];
  const { byKey } = buildTree(folders);
  const out = [];
  let node = byKey.get(keyOf(id));
  const seen = new Set();
  while (node && !seen.has(node.key)) {
    seen.add(node.key);
    out.unshift(node.folder);
    node = node.parentKey != null ? byKey.get(node.parentKey) : null;
  }
  return out;
}

// 전체 경로 글자 — 'A > B > C' (없으면 '').
export function pathText(folders, id) {
  return pathOf(folders, id).map((f) => f.name).join(' > ');
}

// id 가 ancestorId 자신이거나 그 하위인가.
export function isInSubtree(folders, ancestorId, id) {
  if (ancestorId == null || id == null) return false;
  return pathOf(folders, id).some((f) => keyOf(f.id) === keyOf(ancestorId));
}

// 그 폴더(자기 포함) 하위 트리의 높이 — 자기 자신 = 1.
export function subtreeHeight(folders, id) {
  const { byKey } = buildTree(folders);
  const height = (node) => 1 + Math.max(0, ...node.children.map(height));
  const node = byKey.get(keyOf(id));
  return node ? height(node) : 1;
}

// 폴더 행에 보일 단추 — 서버가 준 can_* 를 먼저 쓰고, 없으면(트리 도입 전 모양·목) 기본값으로 계산한다.
// can_share = role 'owner' 이고 1단계 · can_manage = role 'owner' · can_create_child = owner/edit 이고 3단계 미만.
export function folderCaps(f, depthHint) {
  const role = f?.role;
  const depth = Number.isInteger(f?.depth) ? f.depth : (Number.isInteger(depthHint) ? depthHint : 1);
  const isRoot = f?.parent_id == null;
  return {
    canShare: typeof f?.can_share === 'boolean' ? f.can_share : (role === 'owner' && isRoot),
    canManage: typeof f?.can_manage === 'boolean' ? f.can_manage : role === 'owner',
    canCreateChild: typeof f?.can_create_child === 'boolean'
      ? f.can_create_child
      : ((role === 'owner' || role === 'edit') && depth < MAX_FOLDER_DEPTH),
  };
}

// 노트를 넣을 수 있는 폴더인가(이동·새 노트 대상) — 편집 이상.
export function canWriteFolder(f) {
  return f?.role === 'owner' || f?.role === 'edit';
}

// 펼침 상태 — localStorage 'gw.meetings.folderExpanded' 에 펼친 폴더 id 배열로 둔다(읽기 실패 = 빈 값).
export function readExpanded() {
  try {
    const raw = window.localStorage.getItem(FOLDER_EXPANDED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.map(keyOf)) : new Set();
  } catch (e) {
    return new Set();
  }
}

export function writeExpanded(set) {
  try {
    window.localStorage.setItem(FOLDER_EXPANDED_KEY, JSON.stringify(Array.from(set || []).map(keyOf)));
  } catch (e) { /* 저장소 접근 불가면 이번 화면에서만 유지 */ }
}

// select 옵션 글자 — NBSP(U+00A0) 두 칸 × (depth-1) 들여쓰기 + 이름.
export function indentedName(name, depth) {
  const d = Math.max(1, Number(depth) || 1);
  return '  '.repeat(d - 1) + (name || '');
}

// 공유 상태가 바뀌는 이동의 확인 문구(서버 share_change) — 없으면 ''.
export function shareChangeText(change) {
  if (!change) return '';
  const parts = [];
  const gained = Number(change.gained_user_count) || 0;
  const lost = Number(change.lost_user_count) || 0;
  if (gained > 0) parts.push(`${gained}명과 공유 중입니다. 옮기면 이 사용자들도 노트를 볼 수 있습니다.`);
  if (lost > 0) parts.push(`옮기면 다른 사용자 ${lost}명에게서 사라집니다.`);
  return parts.join('\n');
}

// 폴더 삭제 확인 문구 — 서버 미리보기(GET /folders/{id}/delete-preview) 기준.
// 미리보기를 못 받으면(preview 없음) 예전 문구를 그대로 쓴다.
// 노트 M개 = 실제로 휴지통에 가는 노트(전체 − 작성자 미분류로 옮겨질 남의 노트).
export function folderDeleteMessage(name, preview) {
  if (!preview || typeof preview !== 'object') return `'${name}' 폴더와 그 안의 노트를 휴지통으로 보낼까요?`;
  const lines = [`'${name}' 폴더를 휴지통으로 보낼까요?`];
  const folders = Number(preview.folder_count) || 0;
  const others = Number(preview.other_note_count) || 0;
  const notes = Math.max(0, (Number(preview.note_count) || 0) - others);
  if (folders > 0 || notes > 0) lines.push(`하위 폴더 ${folders}개, 노트 ${notes}개도 함께 삭제됩니다.`);
  if (others > 0) lines.push(`다른 사람이 쓴 노트 ${others}개는 삭제되지 않고 작성자의 미분류로 이동합니다.`);
  return lines.join('\n');
}
